<template>
  <v-dialog
    :value="visible"
    width="700"
    persistent
    @input="$emit('update:visible', $event)"
  >
    <v-card>
      <v-card-title class="primary white--text">
        {{ editMode ? "Edit Fee Schedule" : "Add Fee Schedule" }}

        <v-spacer></v-spacer>

        <v-btn icon @click="close">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-3 pb-0">
        <v-form ref="form" v-model="valid">
          <v-row>
            <!-- Programs -->
            <v-col cols="6">
              <v-select
                v-model="form.program_id"
                :items="programsWithAll"
                :item-text="getItemText"
                item-value="id"
                label="Program*"
                outlined
                dense
                multiple
                chips
                hide-details="auto"
                :return-object="false"
                :rules="[rules.required]"
                @change="onProgramChange"
              >
                <template v-slot:selection="{ item, index }">
                  <!-- ALL -->
                  <v-chip v-if="item.id === 'all'" small color="primary" label>
                    All Programs
                  </v-chip>

                  <!-- NORMAL -->
                  <v-chip
                    v-else-if="!form.program_id.includes('all') && index < 2"
                    small
                    color="primary"
                    label
                  >
                    {{ getProgramDisplayText(item) }}
                  </v-chip>

                  <!-- MORE -->
                  <span
                    v-if="!form.program_id.includes('all') && index === 2"
                    class="grey--text caption"
                  >
                    +{{ form.program_id.length - 2 }} more
                  </span>
                </template>
              </v-select>
            </v-col>

            <!-- Partners -->
            <v-col cols="6">
              <v-select
                v-model="form.partner_id"
                :items="partnersWithAll"
                item-text="name"
                item-value="id"
                label="Partner"
                outlined
                dense
                multiple
                chips
                clearable
                hide-details="auto"
                :return-object="false"
                @change="onPartnerChange"
              >
                <template v-slot:selection="{ item, index }">
                  <!-- ALL -->
                  <v-chip v-if="item.id === 'all'" small color="primary" label>
                    All Partners
                  </v-chip>

                  <!-- NORMAL -->
                  <v-chip
                    v-else-if="!form.partner_id.includes('all') && index < 2"
                    small
                    color="primary"
                    label
                  >
                    {{ item.name }}
                  </v-chip>

                  <!-- MORE -->
                  <span
                    v-if="!form.partner_id.includes('all') && index === 2"
                    class="grey--text caption"
                  >
                    +{{ form.partner_id.length - 2 }} more
                  </span>
                </template>
              </v-select>
            </v-col>

            <!-- Year -->
            <v-col cols="6">
              <v-autocomplete
                v-model="form.year"
                label="Year*"
                :items="years"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <!-- Batch -->
            <v-col cols="6">
              <v-autocomplete
                v-model="form.batch"
                label="Batch*"
                :items="[1, 2]"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required]"
              />
            </v-col>

            <!-- Amount -->
            <v-col cols="6">
              <v-text-field
                v-model="form.amount"
                label="Amount *"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.required, rules.number]"
              />
            </v-col>

            <!-- Enrollment End -->
            <v-col cols="6">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedDate"
                    label="Enrollment End Date*"
                    append-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    hide-details="auto"
                    :rules="[rules.required]"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>

                <v-date-picker
                  v-model="form.enrollment_end"
                  :min="today"
                  @input="dateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="pb-3">
        <v-spacer></v-spacer>

        <v-btn text @click="close"> Cancel </v-btn>

        <v-btn color="primary" :disabled="!valid" @click="save">
          {{ editMode ? "Update" : "Save" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  name: "FeeScheduleDialog",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    editMode: {
      type: Boolean,
      default: false,
    },

    feeSchedule: {
      type: Object,
      default: () => ({}),
    },

    programs: {
      type: Array,
      default: () => [],
    },

    partners: {
      type: Array,
      default: () => [],
    },

    getProgramDisplayText: {
      type: Function,
      required: true,
    },
  },

  emits: ["update:visible", "save", "close"],

  data() {
    return {
      rules,
      valid: true,
      dateMenu: false,

      form: {
        program_id: [],
        partner_id: [],
        year: null,
        batch: null,
        amount: null,
        enrollment_end: null,
      },
      years: Array.from({ length: 8 }, (_, k) =>  new Date().getFullYear() -3 + k),
    };
  },

  computed: {
    today() {
      return new Date().toISOString().substr(0, 10);
    },

    programsWithAll() {
      return [
        {
          id: "all",
          name: "All Programs",
        },
        ...this.programs,
      ];
    },

    partnersWithAll() {
      return [
        {
          id: "all",
          name: "All Partners",
        },
        ...this.partners,
      ];
    },
  formattedDate() {
      if (!this.form.enrollment_end) return "";
      const [year, month, day] = this.form.enrollment_end.split("-");
      return `${day}-${month}-${year}`;
    },
 },
  watch: {
    feeSchedule: {
      immediate: true,
      deep: true,

      handler(val) {
        const allProgramIds = this.programs.map((program) => program.id);

        const allPartnerIds = this.partners.map((partner) => partner.id);

        const selectedProgramIds = val?.program_id || [];
        const selectedPartnerIds = val?.partner_id || [];

        const isAllProgramsSelected =
          selectedProgramIds.length > 0 &&
          selectedProgramIds.length === allProgramIds.length;

        const isAllPartnersSelected =
          selectedPartnerIds.length > 0 &&
          selectedPartnerIds.length === allPartnerIds.length;

        this.form = {
          id: val?.id || null,
          program_id: isAllProgramsSelected ? ["all"] : selectedProgramIds,

          partner_id: isAllPartnersSelected ? ["all"] : selectedPartnerIds,

          year: val?.year || null,
          batch: val?.batch || null,
          amount: val?.amount || null,
          enrollment_end: val?.enrollment_end || null,
        };
      },
    },

    visible(val) {
      if (!val) {
        this.resetForm();
      }
    },

    programs: {
      handler() {
        this.prefillEditSelections();
      },
      deep: true,
    },

    partners: {
      handler() {
        this.prefillEditSelections();
      },
      deep: true,
    },
  },

  methods: {
    prefillEditSelections() {
      if (!this.editMode || !this.feeSchedule) return;

      const allProgramIds = this.programs.map((program) => program.id);

      const allPartnerIds = this.partners.map((partner) => partner.id);

      const selectedProgramIds = this.feeSchedule?.program_id || [];

      const selectedPartnerIds = this.feeSchedule?.partner_id || [];

      const isAllProgramsSelected =
        selectedProgramIds.length > 0 &&
        selectedProgramIds.length === allProgramIds.length;

      const isAllPartnersSelected =
        selectedPartnerIds.length > 0 &&
        selectedPartnerIds.length === allPartnerIds.length;

      this.form.program_id = isAllProgramsSelected
        ? ["all"]
        : selectedProgramIds;

      this.form.partner_id = isAllPartnersSelected
        ? ["all"]
        : selectedPartnerIds;
    },
    getItemText(item) {
      if (item.id === "all") {
        return item.name;
      }

      return this.getProgramDisplayText(item);
    },

    handleAllSelection(selectedValues, fieldName) {
      if (!Array.isArray(selectedValues)) return;

      const hasAll = selectedValues.includes("all");

      if (hasAll && selectedValues.length === 1) {
        this.form[fieldName] = ["all"];
        return;
      }

      if (hasAll && selectedValues.length > 1) {
        const lastSelected = selectedValues[selectedValues.length - 1];

        if (lastSelected !== "all") {
          this.form[fieldName] = selectedValues.filter(
            (item) => item !== "all",
          );
        } else {
          this.form[fieldName] = ["all"];
        }
      }
    },

    onProgramChange(values) {
      this.handleAllSelection(values, "program_id");
    },

    onPartnerChange(values) {
      this.handleAllSelection(values, "partner_id");
    },

    save() {
      if (!this.$refs.form.validate()) return;

      const allProgramIds = this.programs.map((program) => program.id);

      const allPartnerIds = this.partners.map((partner) => partner.id);


      const payload = {
        ...this.form,

        program_id: this.form.program_id.includes("all")
          ? allProgramIds
          : this.form.program_id,

        partner_id: this.form.partner_id.includes("all")
          ? allPartnerIds
          : this.form.partner_id,
      };

      this.$emit("save", payload);
    },

    close() {
      this.$emit("close");
      this.$emit("update:visible", false);
    },

    resetForm() {
      this.form = {
        program_id: [],
        partner_id: [],
        year: null,
        batch: null,
        amount: null,
        enrollment_end: null,
      };

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
.v-chip {
  max-width: 180px;
}
</style>
